import React, { useCallback } from 'react';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import FacebookLogin from 'react-facebook-login';
import { BsFacebook, BsApple } from 'react-icons/bs';
import { FcGoogle } from 'react-icons/fc';
import AppleLogin from 'react-apple-login';
import { OrderingSelectors } from 'polygon-ordering';
import setLocalValue from '../utils/setLocalValue';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { IN_DEVELOPMENT } from '../constants';
import redcatApiFetch from '../api';
import { registerUser } from '../utils/analytics';
import { syncMember } from '../thunks/syncMember';
import { setCurrentModal } from '../thunks/setCurrentModal';
import HorizontalLine from '../components/HorizontalLine';
import { OAUTH_EXTRA_INFO_MODAL_ID } from '../modals/OAuthExtraInfoModal';
import { enqueueErrorSnackbar } from '../utils/snackbar';
import {
  useRedcatAnalytics,
  trackSigninSucceeded,
  trackOAuthFlowStarted,
  trackOAuthProviderAuthenticated,
  trackOAuthFlowFailed,
} from '../analytics';
import type { SignupMethod, OAuthSourceScreen } from '../analytics';

const { getOrderTotals, getStagedPurchases, getOAuthExtraInformationConfig, getLocation } =
  OrderingSelectors;

// useGoogleLogin is a hook, so it must be called by a component rendered *inside*
// GoogleOAuthProvider - it can't be called by the component that renders the provider
// itself, which is why this can't just live inline in OAuthLogin below.
const GoogleLoginButton = ({
  sourceScreen,
  analyticsTrack,
  oauthVerify,
  onOAuthAttemptStart,
  onOAuthAttemptEnd,
}: {
  sourceScreen: OAuthSourceScreen;
  analyticsTrack: ReturnType<typeof useRedcatAnalytics>['track'];
  oauthVerify: (response: any, path: string, loginMethod: SignupMethod) => void;
  onOAuthAttemptStart?: () => void;
  onOAuthAttemptEnd?: () => void;
}) => {
  const handleGoogleError = useCallback(() => {
    trackOAuthFlowFailed(analyticsTrack, {
      provider: 'google',
      errorType: 'provider_error',
      stageReached: 'provider_request',
    });
    onOAuthAttemptEnd?.();
  }, [analyticsTrack, onOAuthAttemptEnd]);

  const googleLogin = useGoogleLogin({
    flow: 'auth-code',
    onSuccess: codeResponse => {
      trackOAuthProviderAuthenticated(analyticsTrack, { provider: 'google' });
      oauthVerify(
        { code: codeResponse.code, redirectURI: 'postmessage' },
        '/sso/google/redirect',
        'oauth_google',
      );
    },
    onError: handleGoogleError,
  });

  return (
    <button
      style={styles.googleButton}
      onClick={() => {
        trackOAuthFlowStarted(analyticsTrack, { provider: 'google', sourceScreen });
        onOAuthAttemptStart?.();
        googleLogin();
      }}
    >
      <FcGoogle style={styles.googleIcon} />
      Continue with Google
    </button>
  );
};

const OAuthLogin = ({
  sourceScreen,
  onLoginSucceeded,
  onOAuthAttemptStart,
  onOAuthAttemptEnd,
}: {
  sourceScreen: OAuthSourceScreen;
  onLoginSucceeded?: () => void;
  onOAuthAttemptStart?: () => void;
  onOAuthAttemptEnd?: () => void;
}) => {
  const dispatch = useAppDispatch();
  const analytics = useRedcatAnalytics();
  const orderTotals = useAppSelector(getOrderTotals);
  const stagedPurchases = useAppSelector(getStagedPurchases);
  const googleLoginButtonClientKey = useAppSelector(
    state => state.config.googleLoginButtonClientKey,
  );
  const facebookLoginButtonClientId = useAppSelector(
    state => state.config.facebookLoginButtonClientId,
  );
  const appleLoginButtonClientKey = useAppSelector(state => state.config.appleLoginButtonClientKey);
  const enableGoogleLogin = useAppSelector(state => state.config.enableGoogleLogin);
  const enableAppleLogin = useAppSelector(state => state.config.enableAppleLogin);
  const enableFacebookLogin = useAppSelector(state => state.config.enableFacebookLogin);
  const location = useAppSelector(getLocation);
  const oAuthExtraInformationConfig = useAppSelector(getOAuthExtraInformationConfig);
  const { enableOAuthAdditionalDetails } = oAuthExtraInformationConfig || {};

  const oauthVerify = async (response: any, path: string, loginMethod: SignupMethod) => {
    try {
      const res = await redcatApiFetch({
        path,
        method: 'POST',
        body: { response },
      });

      // Mark the login as succeeded before anything below (e.g. switching to the
      // extra-info modal for a new user) can unmount this tree.
      onLoginSucceeded?.();

      if (res.data.NewUser) {
        if (enableOAuthAdditionalDetails) dispatch(setCurrentModal(OAUTH_EXTRA_INFO_MODAL_ID));
        registerUser({
          content_name: res.data.Provider,
          status: res.success,
          value: orderTotals,
          items: stagedPurchases.map(purchaseItem => purchaseItem.item),
          store: location?.name,
        });
      }
      //@ts-ignore
      if (IN_DEVELOPMENT && res.token) {
        //@ts-ignore
        setLocalValue('memberAuthToken', res.token);
      }
      if (res.data.MemberNo) {
        const memberNumber = res.data.MemberNo;
        analytics.alias(memberNumber);
        analytics.identify({ userId: memberNumber });
      }
      trackSigninSucceeded(analytics.track, { loginMethod });
      dispatch(syncMember());
    } catch (e: any) {
      onOAuthAttemptEnd?.();
      enqueueErrorSnackbar(e.message);
    }
  };

  const facebookLogin = async (response: any) => {
    trackOAuthProviderAuthenticated(analytics.track, { provider: 'facebook' });
    oauthVerify(response, '/sso/facebook/redirect', 'oauth_facebook');
  };

  const handleFacebookFailure = (response: { status?: string }) => {
    trackOAuthFlowFailed(analytics.track, {
      provider: 'facebook',
      errorType: response.status === 'facebookNotLoaded' ? 'provider_error' : 'user_cancelled',
      stageReached: 'provider_request',
    });
    onOAuthAttemptEnd?.();
  };

  const responseAppleLogin = (response: any) => {
    if (response.authorization) {
      response.authorization.redirectURI =
        window.location.protocol + '//' + window.location.host + window.location.pathname;
    }

    if (response.error) {
      trackOAuthFlowFailed(analytics.track, {
        provider: 'apple',
        errorType: ['user_cancelled_authorize', 'popup_closed_by_user'].includes(response.error)
          ? 'user_cancelled'
          : 'provider_error',
        stageReached: 'provider_request',
      });
      onOAuthAttemptEnd?.();
      return;
    }

    trackOAuthProviderAuthenticated(analytics.track, { provider: 'apple' });
    oauthVerify(response, '/sso/apple/redirect', 'oauth_apple');
  };
  const showHorizantalLine =
    (appleLoginButtonClientKey ||
      facebookLoginButtonClientId ||
      googleLoginButtonClientKey !== null) &&
    (enableAppleLogin || enableFacebookLogin || enableGoogleLogin);

  return (
    <>
      {showHorizantalLine && <HorizontalLine text="or" />}

      <div style={styles.container}>
        {enableGoogleLogin && googleLoginButtonClientKey && (
          <GoogleOAuthProvider clientId={googleLoginButtonClientKey}>
            <div style={styles.googleButtonContainer}>
              <GoogleLoginButton
                sourceScreen={sourceScreen}
                analyticsTrack={analytics.track}
                oauthVerify={oauthVerify}
                onOAuthAttemptStart={onOAuthAttemptStart}
                onOAuthAttemptEnd={onOAuthAttemptEnd}
              />
            </div>
          </GoogleOAuthProvider>
        )}

        {enableFacebookLogin && facebookLoginButtonClientId && (
          <div style={styles.facebookButtonContainer}>
            <FacebookLogin
              appId={facebookLoginButtonClientId}
              // autoLoad={true}
              fields="name,email"
              callback={facebookLogin}
              onFailure={handleFacebookFailure}
              onClick={() => {
                trackOAuthFlowStarted(analytics.track, { provider: 'facebook', sourceScreen });
                onOAuthAttemptStart?.();
              }}
              textButton="Continue with Facebook"
              cssClass="facebookLoginButton"
              icon={<BsFacebook style={styles.facebookIcon} />}
            />
          </div>
        )}
        {enableAppleLogin && appleLoginButtonClientKey && (
          <div style={styles.appleButtonContainer}>
            <AppleLogin
              scope="email name"
              {...{
                clientId: appleLoginButtonClientKey,
                redirectURI:
                  window.location.protocol + '//' + window.location.host + window.location.pathname,
              }}
              usePopup={true}
              responseType="code id_token"
              responseMode="form_post"
              render={(renderProps: any) => (
                <button
                  style={styles.appleButton}
                  onClick={e => {
                    trackOAuthFlowStarted(analytics.track, { provider: 'apple', sourceScreen });
                    onOAuthAttemptStart?.();
                    renderProps.onClick(e);
                  }}
                >
                  <BsApple style={styles.appleIcon} />
                  Continue with Apple
                </button>
              )}
              callback={responseAppleLogin}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default OAuthLogin;

const styles: Styles = {
  container: {
    marginTop: 34,
    gap: 15,
    display: 'flex',
    flexDirection: 'column',
  },
  // do this otherwise any children without a minSize will
  // be squashed if there isn't room (instead of overflow scroll)
  googleButtonContainer: {
    width: '100%',
  },
  googleButton: {
    width: '100%',
    backgroundColor: 'white',
    padding: 10,
    border: '1px solid rgba(0, 0, 0, 0.2)',
    borderRadius: '4px',
    fontSize: '14px',
    lineHeight: '16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  googleIcon: {
    marginRight: 10,
    fontSize: '18px',
  },
  facebookButtonContainer: {
    width: '100%',
  },
  facebookIcon: {
    color: 'blue',
    marginRight: 10,
    fontSize: '18px',
    verticalAlign: 'middle',
  },
  appleButtonContainer: {
    width: '100%',
  },
  appleButton: {
    width: '100%',
    backgroundColor: 'white',
    padding: 10,
    border: '1px solid rgba(0, 0, 0, 0.2)',
    borderRadius: '4px',
    fontSize: '14px',
    lineHeight: '16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  appleIcon: {
    marginRight: 10,
    fontSize: '18px',
  },
};
